import { supabase } from "lib/supabase";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import mammoth from "mammoth";

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export const ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const BUCKET = "org_documents";
const SIGNED_URL_EXPIRY = 60 * 60;

export const getDocCode = (titleId, typeId) => `t${titleId}-ty${typeId}`;

export const getStorageTypeId = (selectedId, subTypes) => {
  if (!selectedId) return null;
  const sub = subTypes.find((st) => st.id === selectedId);
  return sub ? sub.docs_type_id : selectedId;
};

export const sanitizeFileName = (name) =>
  name.trim().replace(/[^a-zA-Z0-9._-]/g, "_");

export const buildTimestampPath = () =>
  new Date().toISOString().slice(0, 19).replace("T", "_").replace(/:/g, "-");

export const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) return "File is too large (max 10 MB)";
  if (!ALLOWED_TYPES.has(file.type)) return "Only PDF, Word, JPG, PNG allowed";
  return null;
};

export const getSignedUrl = async (filePath) => {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(filePath, SIGNED_URL_EXPIRY);
  if (error) throw error;
  return data.signedUrl;
};

export const fetchDocsData = async () => {
  const [titlesRes, typesRes, subTypesRes] = await Promise.all([
    supabase.from("ms_docs_title").select("id, name"),
    supabase.from("ms_docs_type").select("id, name, docs_title_id"),
    supabase
      .from("ms_docs_sub_type")
      .select("id, name, docs_title_id, docs_type_id, is_mandatory")
      .order("name"),
  ]);

  if (titlesRes.error) throw titlesRes.error;
  if (typesRes.error) throw typesRes.error;
  if (subTypesRes.error) throw subTypesRes.error;

  return {
    titles: titlesRes.data || [],
    types: typesRes.data || [],
    subTypes: subTypesRes.data || [],
  };
};

export const fetchOrgDocuments = async (orgId, titleId, typeNameMap) => {
  const { data, error } = await supabase
    .from("org_documents")
    .select(
      "id, file_name, file_path, file_type, docs_title_id, docs_type_id, docs_sub_type_id, created_at",
    )
    .eq("org_id", orgId)
    .eq("is_active", 0)
    .eq("docs_title_id", titleId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  const uploads = {};
  data.forEach((doc) => {
    // ✅ always key by parent docs_type_id
    const docCode = getDocCode(doc.docs_title_id, doc.docs_type_id);
    if (!uploads[docCode]) uploads[docCode] = [];

    // label shown in the file row uses sub_type name if available, else type name
    const labelId = doc.docs_sub_type_id || doc.docs_type_id;

    uploads[docCode].push({
      id: doc.id,
      name: doc.file_name,
      filePath: doc.file_path,
      docTypeName: typeNameMap[labelId] || "",
      // carry sub_type_id so grouping can match sub-items correctly
      docsSubTypeId: doc.docs_sub_type_id,
      docsTypeId: doc.docs_type_id,
      createdAt: doc.created_at,
    });
  });

  return uploads;
};

export const uploadOrgDocument = async ({
  file,
  orgId,
  customFileName,
  activeTitle,
  selectedTypeId,
  subTypes,
  typeNameMap,
  existingNames = [],
}) => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "pdf";
  const finalName = sanitizeFileName(customFileName);
  const fullFileName = `${finalName}.${ext}`;

  if (existingNames.includes(fullFileName.toLowerCase())) {
    throw new Error("A document with this name already exists");
  }

  // storageTypeId is always the parent type id
  const storageTypeId = getStorageTypeId(selectedTypeId, subTypes);
  console.log(subTypes);
  const isSubType = subTypes.some(
    (s) => s.id === selectedTypeId && s.docs_title_id === activeTitle.id,
  );
  const typeId = subTypes.find(
    (s) => s.id === selectedTypeId && s.docs_title_id === activeTitle.id,
  ); //For subtype case
  const docs_title_id = activeTitle.id;
  const docs_type_id = isSubType ? typeId.docs_type_id : selectedTypeId; // parent type id
  const docs_sub_type_id = isSubType ? selectedTypeId : null;
  // VERSION CHECK
  let versionQuery = supabase
    .from("org_documents")
    .select("version")
    .eq("org_id", orgId)
    .eq("docs_title_id", docs_title_id)
    .eq("docs_type_id", docs_type_id);

  if (docs_sub_type_id) {
    versionQuery = versionQuery.eq("docs_sub_type_id", docs_sub_type_id);
  } else {
    versionQuery = versionQuery.is("docs_sub_type_id", null);
  }

  const { data: existingDocs, error: versionError } = await versionQuery;

  // NEXT VERSION
  const version = (existingDocs?.length || 0) + 1;

  // FINAL PATH
  const filePath =
    `${orgId}/` +
    `${docs_title_id}/` +
    `${docs_type_id}/` +
    `${docs_sub_type_id || "NA"}/` +
    `v${version}_${fullFileName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, { cacheControl: "3600", upsert: false });
  if (uploadError) throw uploadError;

  const { data: inserted, error: dbError } = await supabase
    .from("org_documents")
    .insert({
      org_id: orgId,
      docs_title_id,
      docs_type_id,
      docs_sub_type_id,
      file_name: fullFileName,
      file_path: filePath,
      file_size: file.size,
      file_type: file.type || `application/${ext}`,
      is_active: 0,
      version,
    })
    .select()
    .single();
  if (dbError) throw dbError;

  const labelId = docs_sub_type_id || docs_type_id;

  return {
    // ✅ docCode uses parent docs_type_id — matches fetchOrgDocuments keying
    docCode: getDocCode(docs_title_id, docs_type_id),
    newEntry: {
      id: inserted.id,
      name: fullFileName,
      filePath,
      docTypeName: typeNameMap[labelId] || "",
      docsSubTypeId: docs_sub_type_id,
      docsTypeId: docs_type_id,
      createdAt: inserted.created_at,
    },
  };
};

export const softDeleteOrgDocument = async (docId) => {
  const { error } = await supabase
    .from("org_documents")
    .update({ is_active: 1 })
    .eq("id", docId);
  if (error) throw error;
};

export const groupUploadedItems = (
  types,
  subTypes,
  activeTitleId,
  uploads,
  getSubTypesForType,
) => {
  const grouped = {};

  types
    .filter((t) => t.docs_title_id === activeTitleId)
    .forEach((type) => {
      // docCode for this type always uses parent type id
      const docCode = getDocCode(activeTitleId, type.id);
      const allFiles = uploads[docCode] || [];
      if (!allFiles.length) return;

      const subs = getSubTypesForType(type.id);

      if (subs.length > 0) {
        // Split files into per-sub-type buckets
        const subBuckets = {};
        const noSubFiles = [];

        allFiles.forEach((file) => {
          if (file.docsSubTypeId) {
            if (!subBuckets[file.docsSubTypeId])
              subBuckets[file.docsSubTypeId] = [];
            subBuckets[file.docsSubTypeId].push(file);
          } else {
            noSubFiles.push(file);
          }
        });

        const subItems = [];

        subs.forEach((sub) => {
          const files = subBuckets[sub.id] || [];
          if (!files.length) return;
          subItems.push({ subId: sub.id, subName: sub.name, docCode, files });
        });

        if (noSubFiles.length) {
          subItems.push({
            subId: type.id,
            subName: type.name,
            docCode,
            files: noSubFiles,
          });
        }

        if (subItems.length) {
          grouped[type.id] = { id: type.id, name: type.name, subItems };
        }
      } else {
        grouped[type.id] = {
          id: type.id,
          name: type.name,
          subItems: [
            { subId: type.id, subName: type.name, docCode, files: allFiles },
          ],
        };
      }
    });

  return Object.values(grouped);
};
export const generateAndUploadDocsPdf = async (
  orgId,
  titleNameMap,
  typeNameMap,
) => {
  console.log("running");
  const { data: docs, error } = await supabase
    .from("org_documents")
    .select(
      "id, file_name, file_path, file_type, created_at, docs_type_id, docs_sub_type_id, docs_title_id",
    )
    .eq("org_id", orgId)
    .eq("is_active", 0)
    .order("docs_title_id")
    .order("docs_type_id")
    .order("docs_sub_type_id")
    .limit(10000);

  if (error) throw error;
  // ── Fetch resumes via personnel belonging to this org ────────
  // Step 1: fetch id from user_profiles
  // REPLACE WITH:
  // Step 1: get memberships for this org
  const { data: memberships, error: membershipError } = await supabase
    .from("user_org_memberships")
    .select("id, user_id")
    .eq("organization_id", orgId);

  if (membershipError) throw membershipError;

  const membershipIds = (memberships || []).map((m) => m.id);
  const membershipUserMap = Object.fromEntries(
    (memberships || []).map((m) => [m.id, m.user_id]),
  );

  // Step 2: get personnel rows via membership_id
  const { data: personnelRows, error: personnelError } = await supabase
    .from("personnel")
    .select("id, membership_id")
    .in("membership_id", membershipIds);

  if (personnelError) throw personnelError;

  const personnelIds = (personnelRows || []).map((p) => p.id);

  // Build maps
  let personnelProfiles = [];
  let personnelMap = {};

  // REPLACE WITH:
  if (membershipIds.length && personnelIds.length) {
    // Fetch full_name from user_profiles for each user_id in memberships
    const userIds = (memberships || []).map((m) => m.user_id);
    const { data: userProfiles } = await supabase
      .from("user_profiles")
      .select("id, full_name")
      .in("id", userIds);

    const userProfileMap = Object.fromEntries(
      (userProfiles || []).map((p) => [p.id, p.full_name]),
    );

    personnelMap = Object.fromEntries(
      (personnelRows || []).map((p) => [
        p.id,
        {
          full_name:
            userProfileMap[membershipUserMap[p.membership_id]] || "Unknown",
        },
      ]),
    );

    // Step 4: fetch personnel_profiles (bio content)
    const { data: profileData, error: profileError } = await supabase
      .from("personnel_profiles")
      .select(
        "id, personnel_id, profile_name, bio_content, skills, certifications, is_default",
      )
      .in("personnel_id", personnelIds);

    if (profileError) throw profileError;
    personnelProfiles = profileData || [];
  }

  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const PAGE_WIDTH = 595;
  const PAGE_HEIGHT = 842;
  const MARGIN = 40;

  // ── current page state (shared across all renders) ───────────
  let currentPage = null;
  let currentY = 0;

  // ── add a brand-new page and update state ────────────────────
  const addPage = () => {
    currentPage = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    currentY = PAGE_HEIGHT - 50;
  };

  // ── ensure there is enough vertical room; add page if not ───
  const ensureSpace = (needed) => {
    if (!currentPage || currentY - needed < 50) {
      addPage();
    }
  };

  // ── word-wrap helper ─────────────────────────────────────────
  const wrapText = (text, maxWidth, fnt, fontSize) => {
    // Accurate wrap using actual font metrics
    const words = text.split(" ");
    const lines = [];
    let current = "";
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      const testWidth = fnt.widthOfTextAtSize(test, fontSize);
      if (testWidth > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  // ── extract plain text from .docx bytes via mammoth ─────────
  const extractDocxText = async (fileBytes) => {
    try {
      const arrayBuffer = fileBytes.buffer.slice(
        fileBytes.byteOffset,
        fileBytes.byteOffset + fileBytes.byteLength,
      );
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value || "";
    } catch {
      return null;
    }
  };

  // ════════════════════════════════════════════════════════════
  // GROUP DOCS
  // ════════════════════════════════════════════════════════════
  const groupedByTitle = {};
  if (docs && docs.length) {
    docs.forEach((d) => {
      const k = d.docs_title_id;
      if (!groupedByTitle[k]) groupedByTitle[k] = [];
      groupedByTitle[k].push(d);
    });
  }

  // ── helper: render one file ──────────────────────────────────
  const renderFile = async ({ f, indent }) => {
    const uploadedDate = new Date(f.created_at).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const fileType = f.file_type || "";
    const maxTextWidth = PAGE_WIDTH - indent - MARGIN - 10;

    let fileBytes = null;
    try {
      const url = await getSignedUrl(f.file_path);
      const res = await fetch(url);
      fileBytes = new Uint8Array(await res.arrayBuffer());
    } catch {
      /* skip */
    }

    // ── bullet + filename ────────────────────────────────────────
    ensureSpace(30);
    currentPage.drawText(`•  ${f.file_name}`, {
      x: indent,
      y: currentY,
      font: boldFont,
      size: 10,
      color: rgb(0.1, 0.1, 0.1),
    });
    currentY -= 13;

    ensureSpace(12);
    currentPage.drawText(`Uploaded: ${uploadedDate}`, {
      x: indent + 10,
      y: currentY,
      font,
      size: 8,
      color: rgb(0.55, 0.55, 0.55),
    });
    currentY -= 14;

    // ── IMAGE ────────────────────────────────────────────────────
    if (fileType === "image/jpeg" || fileType === "image/png") {
      if (fileBytes) {
        try {
          const img =
            fileType === "image/png"
              ? await pdfDoc.embedPng(fileBytes)
              : await pdfDoc.embedJpg(fileBytes);
          const maxW = maxTextWidth;
          const maxH = 300;
          const scale = Math.min(maxW / img.width, maxH / img.height, 1);
          const iW = img.width * scale;
          const iH = img.height * scale;

          ensureSpace(iH + 20);
          currentPage.drawImage(img, {
            x: indent + 10,
            y: currentY - iH,
            width: iW,
            height: iH,
          });
          currentY -= iH + 20;
        } catch {
          ensureSpace(16);
          currentPage.drawText("[Could not render image]", {
            x: indent + 10,
            y: currentY,
            font,
            size: 8,
            color: rgb(0.7, 0.3, 0.3),
          });
          currentY -= 16;
        }
      }

      // ── PDF ──────────────────────────────────────────────────────
    } else if (fileType === "application/pdf") {
      if (fileBytes) {
        try {
          const srcPdf = await PDFDocument.load(fileBytes);
          const count = srcPdf.getPageCount();
          const indices = srcPdf.getPageIndices();
          const copied = await pdfDoc.copyPages(srcPdf, indices);

          copied.forEach((cp, i) => {
            const { height: sh } = cp.getSize();
            cp.drawText(`${f.file_name}  —  Page ${i + 1} of ${count}`, {
              x: 20,
              y: sh - 18,
              font,
              size: 7,
              color: rgb(0.6, 0.6, 0.6),
            });
            pdfDoc.addPage(cp);
          });

          // ✅ KEY FIX: after inserting embedded PDF pages, we MUST
          //    re-point currentPage to a fresh continuation page so
          //    subsequent content isn't drawn on top of the embedded pages.
          addPage();
        } catch {
          ensureSpace(16);
          currentPage.drawText("[Could not embed PDF]", {
            x: indent + 10,
            y: currentY,
            font,
            size: 8,
            color: rgb(0.7, 0.3, 0.3),
          });
          currentY -= 16;
        }
      }

      // ── WORD (.docx) ─────────────────────────────────────────────
    } else if (
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      f.file_name?.toLowerCase().endsWith(".docx")
    ) {
      if (fileBytes) {
        const rawText = await extractDocxText(fileBytes);
        if (rawText) {
          const BOX_PADDING = 8;
          const LINE_HEIGHT = 13;
          const FONT_SIZE = 8.5;
          // No MAX_LINES cap — render the FULL document
          const innerWidth = maxTextWidth - BOX_PADDING * 2 - 6;

          // Split into paragraphs and wrap each one accurately
          const paragraphs = rawText.split("\n").map((p) => p.trim());

          const allLines = [];
          for (const para of paragraphs) {
            if (para.length === 0) {
              allLines.push(""); // blank line between paragraphs
            } else {
              const wrapped = wrapText(para, innerWidth, font, FONT_SIZE);
              allLines.push(...wrapped);
            }
          }

          // ── Render line-by-line, flowing across pages ──────────
          // Header label (first box header)
          let isFirstChunk = true;

          let lineIdx = 0;
          while (lineIdx < allLines.length) {
            // How many lines fit on the remaining space of this page?
            const availableH =
              currentY - 50 - BOX_PADDING * 2 - (isFirstChunk ? 22 : 10);
            const linesFit = Math.max(1, Math.floor(availableH / LINE_HEIGHT));
            const chunk = allLines.slice(lineIdx, lineIdx + linesFit);
            const isLastChunk = lineIdx + chunk.length >= allLines.length;

            const boxHeight =
              chunk.length * LINE_HEIGHT +
              BOX_PADDING * 2 +
              (isFirstChunk ? 22 : 10);
            ensureSpace(boxHeight + 10);

            // Draw box background
            currentPage.drawRectangle({
              x: indent + 10,
              y: currentY - boxHeight,
              width: maxTextWidth,
              height: boxHeight,
              color: rgb(0.97, 0.97, 1.0),
              borderColor: rgb(0.78, 0.8, 0.92),
              borderWidth: 0.8,
            });

            // Label only on the first chunk
            if (isFirstChunk) {
              currentPage.drawText("Word Document — Full Content", {
                x: indent + 16,
                y: currentY - BOX_PADDING - 10,
                font: boldFont,
                size: 7.5,
                color: rgb(0.3, 0.35, 0.7),
              });
            }

            const labelOffset = isFirstChunk ? 22 : 10;
            let textY = currentY - BOX_PADDING - labelOffset;

            for (const line of chunk) {
              if (line !== "") {
                currentPage.drawText(line, {
                  x: indent + 16,
                  y: textY,
                  font,
                  size: FONT_SIZE,
                  color: rgb(0.15, 0.15, 0.15),
                });
              }
              textY -= line === "" ? LINE_HEIGHT * 0.5 : LINE_HEIGHT;
            }

            // "Continued" note if more chunks follow
            if (!isLastChunk) {
              currentPage.drawText("… continued on next page", {
                x: indent + 16,
                y: currentY - boxHeight + BOX_PADDING + 2,
                font: italicFont,
                size: 7,
                color: rgb(0.5, 0.5, 0.5),
              });
            }

            currentY -= boxHeight + 12;
            lineIdx += chunk.length;
            isFirstChunk = false;

            // If more lines remain, move to next page before next chunk
            if (!isLastChunk) {
              addPage();
            }
          }
        } else {
          ensureSpace(16);
          currentPage.drawText("[Could not extract Word content]", {
            x: indent + 10,
            y: currentY,
            font: italicFont,
            size: 8,
            color: rgb(0.7, 0.3, 0.3),
          });
          currentY -= 16;
        }
      }

      // ── OTHER ────────────────────────────────────────────────────
    } else {
      const ext = f.file_name?.split(".").pop()?.toUpperCase() || "FILE";
      ensureSpace(30);
      currentPage.drawRectangle({
        x: indent + 10,
        y: currentY - 14,
        width: maxTextWidth,
        height: 22,
        color: rgb(0.97, 0.96, 0.91),
        borderColor: rgb(0.85, 0.75, 0.4),
        borderWidth: 0.5,
      });
      currentPage.drawText(`[${ext}] — open from the portal to view`, {
        x: indent + 16,
        y: currentY - 6,
        font: italicFont,
        size: 8,
        color: rgb(0.45, 0.3, 0.05),
      });
      currentY -= 30;
    }

    currentY -= 10; // gap after each file
  };
  // ── helper: render bio profile for one personnel ──────────────
  const renderPersonnelProfile = ({ profile, indent }) => {
    const maxTextWidth = PAGE_WIDTH - indent - MARGIN - 10;

    // ── Profile name header ───────────────────────────────────────
    ensureSpace(24);
    currentPage.drawText(
      `${profile.profile_name}${profile.is_default ? "  [Default]" : ""}`,
      {
        x: indent,
        y: currentY,
        font: boldFont,
        size: 10,
        color: rgb(0.1, 0.1, 0.5),
      },
    );
    currentY -= 16;

    // ── Bio content ───────────────────────────────────────────────
    if (profile.bio_content) {
      const BOX_PADDING = 8;
      const LINE_HEIGHT = 13;
      const FONT_SIZE = 8.5;
      const innerWidth = maxTextWidth - BOX_PADDING * 2 - 6;

      const paragraphs = profile.bio_content.split("\n").map((p) => p.trim());
      const allLines = [];
      for (const para of paragraphs) {
        if (para.length === 0) {
          allLines.push("");
        } else {
          const wrapped = wrapText(para, innerWidth, font, FONT_SIZE);
          allLines.push(...wrapped);
        }
      }

      let isFirstChunk = true;
      let lineIdx = 0;
      while (lineIdx < allLines.length) {
        const availableH =
          currentY - 50 - BOX_PADDING * 2 - (isFirstChunk ? 22 : 10);
        const linesFit = Math.max(1, Math.floor(availableH / LINE_HEIGHT));
        const chunk = allLines.slice(lineIdx, lineIdx + linesFit);
        const isLastChunk = lineIdx + chunk.length >= allLines.length;

        const boxHeight =
          chunk.length * LINE_HEIGHT +
          BOX_PADDING * 2 +
          (isFirstChunk ? 22 : 10);
        ensureSpace(boxHeight + 10);

        currentPage.drawRectangle({
          x: indent + 10,
          y: currentY - boxHeight,
          width: maxTextWidth,
          height: boxHeight,
          color: rgb(0.96, 0.98, 0.96),
          borderColor: rgb(0.6, 0.8, 0.65),
          borderWidth: 0.8,
        });

        if (isFirstChunk) {
          currentPage.drawText("Bio", {
            x: indent + 16,
            y: currentY - BOX_PADDING - 10,
            font: boldFont,
            size: 7.5,
            color: rgb(0.2, 0.5, 0.3),
          });
        }

        const labelOffset = isFirstChunk ? 22 : 10;
        let textY = currentY - BOX_PADDING - labelOffset;

        for (const line of chunk) {
          if (line !== "") {
            currentPage.drawText(line, {
              x: indent + 16,
              y: textY,
              font,
              size: FONT_SIZE,
              color: rgb(0.15, 0.15, 0.15),
            });
          }
          textY -= line === "" ? LINE_HEIGHT * 0.5 : LINE_HEIGHT;
        }

        if (!isLastChunk) {
          currentPage.drawText("… continued on next page", {
            x: indent + 16,
            y: currentY - boxHeight + BOX_PADDING + 2,
            font: italicFont,
            size: 7,
            color: rgb(0.5, 0.5, 0.5),
          });
        }

        currentY -= boxHeight + 12;
        lineIdx += chunk.length;
        isFirstChunk = false;
        if (!isLastChunk) addPage();
      }
    }

    // ── Skills ────────────────────────────────────────────────────
    if (profile.skills?.length) {
      ensureSpace(20);
      currentPage.drawText("Skills:", {
        x: indent + 10,
        y: currentY,
        font: boldFont,
        size: 8.5,
        color: rgb(0.2, 0.2, 0.5),
      });
      currentY -= 13;

      const skillsText = profile.skills.join("  •  ");
      const wrappedSkills = wrapText(
        skillsText,
        PAGE_WIDTH - indent - MARGIN - 20,
        font,
        8,
      );
      for (const line of wrappedSkills) {
        ensureSpace(12);
        currentPage.drawText(line, {
          x: indent + 16,
          y: currentY,
          font,
          size: 8,
          color: rgb(0.2, 0.35, 0.6),
        });
        currentY -= 12;
      }
      currentY -= 4;
    }

    // ── Certifications ────────────────────────────────────────────
    if (profile.certifications?.length) {
      ensureSpace(20);
      currentPage.drawText("Certifications:", {
        x: indent + 10,
        y: currentY,
        font: boldFont,
        size: 8.5,
        color: rgb(0.2, 0.2, 0.5),
      });
      currentY -= 13;

      for (const cert of profile.certifications) {
        ensureSpace(12);
        currentPage.drawText(`  -  ${cert}`, {
          x: indent + 16,
          y: currentY,
          font,
          size: 8,
          color: rgb(0.15, 0.15, 0.15),
        });
        currentY -= 12;
      }
      currentY -= 4;
    }

    currentY -= 10;
  };
  // ════════════════════════════════════════════════════════════
  // RENDER ALL CATEGORIES
  // ════════════════════════════════════════════════════════════
  for (const [titleKey, titleDocs] of Object.entries(groupedByTitle)) {
    // ── CATEGORY TITLE ───────────────────────────────────────────
    if (!currentPage) {
      addPage();
    } else if (currentY < 120) {
      addPage();
    }

    currentPage.drawRectangle({
      x: 0,
      y: currentY - 8,
      width: PAGE_WIDTH,
      height: 36,
      color: rgb(0.93, 0.94, 0.99),
    });
    currentPage.drawText(titleNameMap[titleKey] || titleKey, {
      x: MARGIN,
      y: currentY,
      font: boldFont,
      size: 17,
      color: rgb(0.15, 0.18, 0.65),
    });
    currentY -= 34;

    currentPage.drawLine({
      start: { x: MARGIN, y: currentY },
      end: { x: PAGE_WIDTH - MARGIN, y: currentY },
      thickness: 0.5,
      color: rgb(0.2, 0.2, 0.2),
    });
    currentY -= 24;

    // group by type
    const byType = {};
    titleDocs.forEach((d) => {
      const k = d.docs_type_id;
      if (!byType[k]) byType[k] = [];
      byType[k].push(d);
    });

    for (const [typeId, typeFiles] of Object.entries(byType)) {
      const typeName = typeNameMap[typeId] || "Other";
      const hasSubs = typeFiles.some((f) => f.docs_sub_type_id);

      if (hasSubs) {
        ensureSpace(40);
        currentPage.drawText(typeName, {
          x: MARGIN,
          y: currentY,
          font: boldFont,
          size: 13,
          color: rgb(0.1, 0.15, 0.55),
        });
        currentY -= 8;
        currentPage.drawLine({
          start: { x: MARGIN, y: currentY },
          end: { x: MARGIN + 180, y: currentY },
          thickness: 1.2,
          color: rgb(0.4, 0.45, 0.85),
        });
        currentY -= 20;

        const bySub = {};
        typeFiles.forEach((f) => {
          const sk = f.docs_sub_type_id || "__none__";
          if (!bySub[sk]) bySub[sk] = [];
          bySub[sk].push(f);
        });

        for (const [subKey, subFiles] of Object.entries(bySub)) {
          if (subKey !== "__none__") {
            ensureSpace(20);
            currentPage.drawText(`- ${typeNameMap[subKey] || subKey}`, {
              x: MARGIN + 16,
              y: currentY,
              font: boldFont,
              size: 10.5,
              color: rgb(0.25, 0.35, 0.7),
            });
            currentY -= 18;
          }
          for (const f of subFiles) {
            await renderFile({ f, indent: MARGIN + 28 });
          }
          currentY -= 6;
        }
      } else {
        ensureSpace(36);
        currentPage.drawText(typeName, {
          x: MARGIN,
          y: currentY,
          font: boldFont,
          size: 12,
          color: rgb(0.1, 0.15, 0.55),
        });
        currentY -= 8;
        currentPage.drawLine({
          start: { x: MARGIN, y: currentY },
          end: { x: MARGIN + 140, y: currentY },
          thickness: 1,
          color: rgb(0.6, 0.65, 0.95),
        });
        currentY -= 18;

        for (const f of typeFiles) {
          await renderFile({ f, indent: MARGIN + 16 });
        }
      }

      currentY -= 18; // gap between types
    }
  }

  // ── Upload ───────────────────────────────────────────────────
  const pdfBytes = await pdfDoc.save();
  const pdfPath = `${orgId}/masterDocument.pdf`;
  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(pdfPath, pdfBytes, {
      contentType: "application/pdf",
      upsert: true,
      cacheControl: "no-cache",
    });

  if (uploadErr) throw uploadErr;

  const payload = {
    org_id: orgId,
    file_name: "masterDocument.pdf",
    file_path: pdfPath,
    file_type: "application/pdf",
    doc_modified_at: new Date().toISOString(),
  };
  console.log("PAYLOAD", payload);
  // Check if record already exists
  const { data: existingDoc, error: fetchError } = await supabase
    .from("master_documents")
    .select("id")
    .eq("org_id", orgId)
    .eq("is_active", 0)
    .maybeSingle();

  if (fetchError) {
    throw fetchError;
  }

  let masterDocumentId;

  if (existingDoc) {
    const { data: updatedDoc, error: updateError } = await supabase
      .from("master_documents")
      .update(payload)
      .eq("id", existingDoc.id)
      .select("id")
      .single();

    if (updateError) {
      throw updateError;
    }

    masterDocumentId = updatedDoc.id;
  } else {
    const { data: insertedDoc, error: insertError } = await supabase
      .from("master_documents")
      .insert(payload)
      .select("id")
      .single();

    if (insertError) {
      throw insertError;
    }

    masterDocumentId = insertedDoc.id;
  }

  // --------------------------------------------------
  // PREPROCESS MASTER DOCUMENT
  // --------------------------------------------------

  const API_BASE_URL = import.meta.env.VITE_AI_BACKEND_URL;

  const preprocessResponse = await fetch(
    `${API_BASE_URL}/parse-preprocess-master-document`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        orgId,
        masterDocumentId,
      }),
    },
  );

  const preprocessResult = await preprocessResponse.json().catch(() => ({}));

  if (!preprocessResponse.ok) {
    throw new Error(
      preprocessResult.message || "Master document preprocessing failed",
    );
  }

  console.log("Master document preprocessing completed:", preprocessResult);

  return {
    masterDocumentId,
    totalChunks: preprocessResult.totalChunks || 0,
  };
}; // <-- close generateAndUploadDocsPdf HERE

export const getDocsPdfUrl = async (orgId) => {
  const { data, error } = await supabase
    .from("master_documents")
    .select("file_path")
    .eq("org_id", orgId)
    .eq("is_active", 0)
    .maybeSingle();

  if (error || !data?.file_path) return null;

  try {
    return await getSignedUrl(data.file_path);
  } catch {
    return null;
  }
};
